import * as XLSX from 'xlsx';

// Estimativas de consumo (baseadas em equipamentos típicos de grow)
// Luz LED ~ 200W, Ventilação/exaustão ~ 50W, Umidificador ~ 30W
const POWER = { light: 200, ventilation: 50, humidifier: 30 }; // Watts
// Bomba de rega: 220 L/h ≈ 61,11 mL/s. Volume por planta em mL / 61,11 = segundos
const PUMP_ML_PER_SEC = 61.11;

function fmtDate(d) {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt) ? '' : dt.toLocaleDateString('pt-BR');
}

function daysBetween(start, end) {
    if (!start || !end) return 0;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (isNaN(s) || isNaN(e)) return 0;
    return Math.max(0, Math.round((e - s) / 86400000));
}

// Estima consumo de energia (kWh) e água (L) para uma planta
function estimateConsumption(plant) {
    const start = plant.history?.startDate || plant.startDate;
    const end = plant.history?.endDate || plant.endDate || new Date();
    const days = daysBetween(start, end) || 1;
    const hours = days * 24;

    // Luz ligada ~ 18h/dia (vegetativo) ou 12h/dia (floração)
    const lightHoursPerDay = plant.growthStage === 'flowering' ? 12 : 18;
    const lightKwh = (POWER.light * lightHoursPerDay * days) / 1000;
    const ventKwh = (POWER.ventilation * 24 * days) / 1000;
    const humKwh = (POWER.humidifier * 12 * days) / 1000;
    const totalKwh = lightKwh + ventKwh + humKwh;

    // Água: rega em mL por planta, 2x ao dia
    const mlPerWater = plant.history?.wateringAmount || plant.wateringAmount || 100;
    const waterLiters = (mlPerWater * 2 * days) / 1000;

    return {
        days,
        lightKwh: Math.round(lightKwh * 100) / 100,
        ventKwh: Math.round(ventKwh * 100) / 100,
        humKwh: Math.round(humKwh * 100) / 100,
        totalKwh: Math.round(totalKwh * 100) / 100,
        waterLiters: Math.round(waterLiters * 100) / 100
    };
}

export function exportHistoryToExcel({ plants = [], stats = null, deviceId = 'esp32_001' }) {
    const wb = XLSX.utils.book_new();

    // ---- Aba 1: Resumo Geral ----
    const totalPlants = plants.length;
    const active = plants.filter(p => p.status !== 'archived');
    const archived = plants.filter(p => p.status === 'archived');
    const harvested = archived.filter(p => p.endType === 'harvested');
    const killed = archived.filter(p => p.endType === 'killed');

    let totalEnergy = 0, totalWater = 0, totalCultDays = 0;
    plants.forEach(p => {
        const c = estimateConsumption(p);
        totalEnergy += c.totalKwh;
        totalWater += c.waterLiters;
        totalCultDays += c.days;
    });

    const summaryRows = [
        ['AutoGrow — Relatório de Cultivo'],
        ['Dispositivo', deviceId],
        ['Gerado em', new Date().toLocaleString('pt-BR')],
        [],
        ['Indicador', 'Valor'],
        ['Total de plantas', totalPlants],
        ['Plantas ativas', active.length],
        ['Plantas finalizadas', archived.length],
        ['Colheitas', harvested.length],
        ['Plantas perdidas (mortas)', killed.length],
        ['Tempo total cultivado (dias)', totalCultDays],
        ['Energia estimada total (kWh)', Math.round(totalEnergy * 100) / 100],
        ['Água estimada total (L)', Math.round(totalWater * 100) / 100],
        ['Peso úmido total (g)', harvested.reduce((s, p) => s + (p.wetWeight || p.history?.wetWeight || 0), 0)],
        ['Peso seco total (g)', harvested.reduce((s, p) => s + (p.dryWeight || p.history?.dryWeight || 0), 0)]
    ];
    if (stats) {
        summaryRows.push([]);
        summaryRows.push(['Dados do servidor (stats):']);
        summaryRows.push(['Uptime (dias)', stats.uptimeDays]);
        summaryRows.push(['Comandos enviados', stats.totalCommands]);
        summaryRows.push(['Leituras de sensor', stats.sensorReadings]);
    }
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 32 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');

    // ---- Aba 2: Plantas Ativas ----
    const activeHeader = ['Nome', 'Strain', 'Estágio', 'Início', 'Última rega', 'Próxima rega', 'Volume rega (mL)', 'Dias cultivados', 'Energia estim. (kWh)', 'Água estim. (L)'];
    const activeData = active.map(p => {
        const c = estimateConsumption(p);
        return [
            p.name, p.strain, p.growthStage, fmtDate(p.startDate),
            fmtDate(p.lastWatered), fmtDate(p.nextWatering),
            p.wateringAmount || 0, c.days, c.totalKwh, c.waterLiters
        ];
    });
    const wsActive = XLSX.utils.aoa_to_sheet([activeHeader, ...activeData]);
    wsActive['!cols'] = [{ wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsActive, 'Plantas Ativas');

    // ---- Aba 3: Colheitas ----
    const harvestHeader = ['Nome', 'Strain', 'Estágio', 'Início', 'Fim', 'Dias de vida', 'Peso úmido (g)', 'Peso seco (g)', 'Energia estim. (kWh)', 'Água estim. (L)'];
    const harvestData = harvested.map(p => {
        const c = estimateConsumption(p);
        return [
            p.name, p.strain, p.growthStage, fmtDate(p.history?.startDate || p.startDate),
            fmtDate(p.history?.endDate || p.endDate), p.history?.durationDays ?? c.days,
            p.wetWeight || p.history?.wetWeight || 0, p.dryWeight || p.history?.dryWeight || 0,
            c.totalKwh, c.waterLiters
        ];
    });
    const wsHarvest = XLSX.utils.aoa_to_sheet([harvestHeader, ...harvestData]);
    wsHarvest['!cols'] = [{ wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsHarvest, 'Colheitas');

    // ---- Aba 4: Histórico Completo (todas finalizadas) ----
    const histHeader = ['Nome', 'Strain', 'Tipo fim', 'Estágio', 'Início', 'Fim', 'Dias de vida', 'Peso úmido (g)', 'Peso seco (g)', 'Energia estim. (kWh)', 'Água estim. (L)'];
    const histData = archived.map(p => {
        const c = estimateConsumption(p);
        return [
            p.name, p.strain, p.endType === 'killed' ? 'Perdida' : 'Colhida', p.growthStage,
            fmtDate(p.history?.startDate || p.startDate), fmtDate(p.history?.endDate || p.endDate),
            p.history?.durationDays ?? c.days, p.wetWeight || p.history?.wetWeight || 0,
            p.dryWeight || p.history?.dryWeight || 0, c.totalKwh, c.waterLiters
        ];
    });
    const wsHist = XLSX.utils.aoa_to_sheet([histHeader, ...histData]);
    wsHist['!cols'] = [{ wch: 18 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsHist, 'Histórico');

    // ---- Aba 5: Consumo Estimado (detalhado por planta) ----
    const consHeader = ['Nome', 'Dias', 'Luz (kWh)', 'Ventilação (kWh)', 'Umidificador (kWh)', 'Energia total (kWh)', 'Água (L)'];
    const consData = plants.map(p => {
        const c = estimateConsumption(p);
        return [p.name, c.days, c.lightKwh, c.ventKwh, c.humKwh, c.totalKwh, c.waterLiters];
    });
    const wsCons = XLSX.utils.aoa_to_sheet([consHeader, ...consData]);
    wsCons['!cols'] = [{ wch: 18 }, { wch: 8 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsCons, 'Consumo Estimado');

    const fileName = `autogrow_historico_${deviceId}_${new Date().toISOString().slice(0, 10)}.xlsx`;

    // Gera o arquivo e dispara o download no navegador (funciona em browser e Node)
    if (typeof window !== 'undefined' && window.document) {
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else {
        XLSX.writeFile(wb, fileName);
    }
    return fileName;
}
